import '@theme/gsap'
import { useReduced, useReducedChange } from '@theme/maths'
import { useIntersection } from '@theme/use-intersection'

const WipeSliderConfig = {
  autoplayInterval: 5000,
  contentDuration: 0.6,
  contentOffset: 20,
  contentStagger: 0.1,
  firstSlideDelay: 500,
  mobileQuery: '(width <= 799px)',
  nativeSnapDelay: 120,
  progressFadeDelay: 300,
  progressInterval: 50,
  progressRewindDuration: 200,
  resumeDelay: 100,
  swipeThreshold: 40
}

const WipeSliderSelector = {
  content: '[data-wipe-slider="content"]',
  slide: '[data-wipe-slider="slide"]'
}

const WipeSliderTransitionProperties = ['translate', 'clip-path']

function parsePositiveNumber(value, fallback) {
  const parsed = Number.parseFloat(value ?? '')
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback
}

function isRendered(element) {
  return (
    typeof element.checkVisibility !== 'function' || element.checkVisibility()
  )
}

function isEditableTarget(target) {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)
}

class WipeSliderContentAnimator {
  #animations = new Set()
  #splitEntries = new Map()

  animate(content, reducedMotion) {
    this.#killAnimations()
    const items = Array.from(content?.firstElementChild?.children ?? [])
    if (reducedMotion || !(window.gsap && items.length)) return

    const { fallbackTargets, splitTargets } = this.#classifyTargets(items)
    this.#animateSplitTargets(splitTargets)
    this.#animateFallbackTargets(fallbackTargets)
  }

  revert() {
    this.#killAnimations()
    for (const [element, entry] of this.#splitEntries) {
      entry.instance?.revert?.()
      element.classList.remove('hero-split-lines')
    }
    this.#splitEntries.clear()
  }

  #killAnimations() {
    for (const animation of this.#animations) animation.kill()
    this.#animations.clear()
  }

  #classifyTargets(items) {
    const splitTargets = []
    const fallbackTargets = []
    for (const item of items) {
      const targets = this.#createSplitTargets(item)
      if (targets.length) splitTargets.push(...targets)
      else fallbackTargets.push(item)
    }
    return { fallbackTargets, splitTargets }
  }

  #createSplitTargets(element) {
    const cached = this.#splitEntries.get(element)
    if (cached) return cached.targets
    if (!window.SplitText) return []
    if (this.#containsInteractiveContent(element)) return []

    try {
      const instance = this.#createSplitInstance(window.SplitText, element)
      const targets = this.#wrapSplitLines(instance?.lines ?? [])
      if (!targets.length) return []

      element.classList.add('hero-split-lines')
      this.#splitEntries.set(element, { instance, targets })
      return targets
    } catch {
      return []
    }
  }

  #containsInteractiveContent(element) {
    const selector = 'a, button, input, select, textarea, [contenteditable]'
    return element.matches(selector) || element.querySelector(selector) !== null
  }

  #createSplitInstance(SplitText, element) {
    const options = { linesClass: 'single-line', type: 'lines' }
    const created = SplitText.create
      ? SplitText.create(element, options)
      : new SplitText(element, options)
    return Array.isArray(created) ? created[0] : created
  }

  #wrapSplitLines(lines) {
    const targets = []
    for (const line of lines) {
      const inner = document.createElement('span')
      inner.className = 'single-line-inner'
      while (line.firstChild) inner.append(line.firstChild)
      line.append(inner)
      targets.push(inner)
    }
    return targets
  }

  #animateSplitTargets(targets) {
    if (!targets.length) return
    window.gsap.killTweensOf(targets)
    this.#track(
      window.gsap.fromTo(
        targets,
        { autoAlpha: 0, rotate: 0.001, yPercent: 100 },
        {
          autoAlpha: 1,
          duration: WipeSliderConfig.contentDuration,
          ease: 'power4.out',
          rotate: 0.001,
          stagger: WipeSliderConfig.contentStagger,
          yPercent: 0
        }
      )
    )
  }

  #animateFallbackTargets(targets) {
    if (!targets.length) return
    window.gsap.killTweensOf(targets)
    this.#track(
      window.gsap.fromTo(
        targets,
        { autoAlpha: 0, y: WipeSliderConfig.contentOffset },
        {
          autoAlpha: 1,
          clearProps: 'opacity,transform,visibility',
          duration: WipeSliderConfig.contentDuration,
          ease: 'power2.out',
          stagger: WipeSliderConfig.contentStagger,
          y: 0
        }
      )
    )
  }

  #track(animation) {
    if (!animation) return
    this.#animations.add(animation)
    animation.eventCallback('onComplete', () => {
      this.#animations.delete(animation)
    })
  }
}

class WipeSlider extends HTMLElement {
  #autoplayEnabled = false
  #autoplayInterval = WipeSliderConfig.autoplayInterval
  #autoplayTimer = null
  #contentAnimator = new WipeSliderContentAnimator()
  #controlsRoot = null
  #currentIndex = 0
  #events = null
  #isAnimating = false
  #isInViewport = false
  #nativeScrollEndTimer = null
  #nativeSnap = false
  #reducedMotion = false
  #slides = []
  #touchActive = false
  #touchStartX = 0
  #touchStartY = 0
  #track = null
  #transitionTarget = null
  #unobserveViewport = null
  #unwatchReduced = null

  next = () => {
    this.#navigate((this.#currentIndex + 1) % this.#slides.length, 1)
  }

  previous = () => {
    this.#navigate(
      (this.#currentIndex - 1 + this.#slides.length) % this.#slides.length,
      -1
    )
  }

  select = (index, immediate = false) => {
    const direction = index > this.#currentIndex ? 1 : -1
    this.#navigate(index, direction, immediate)
  }

  #handleClick = (event) => {
    const paginationItem = event.target.closest(
      'button[data-wipe-slider-index]'
    )
    if (paginationItem && this.contains(paginationItem)) {
      this.select(Number(paginationItem.dataset.wipeSliderIndex))
      return
    }

    const button = event.target.closest('button[aria-controls]')
    if (button?.getAttribute('aria-controls') !== this.id) return
    if (button.matches('[is="previous-button"]')) this.previous()
    if (button.matches('[is="next-button"]')) this.next()
  }

  #handleBlockSelect = (event) => {
    const blockId = event.detail?.blockId
    const index = this.#slides.findIndex(
      (slide) =>
        slide === event.target ||
        slide.contains(event.target) ||
        (blockId && slide.dataset.blockId === blockId)
    )
    if (index >= 0) this.select(index, true)
  }

  #handleMotionChange = (matches) => {
    this.#reducedMotion = matches
    this.#contentAnimator.revert()
    this.#showImmediately(this.#currentIndex)
  }

  #handleBreakpointChange = () => {
    const slide = this.#slides[this.#currentIndex]
    if (slide) this.#syncSlideVideos(slide, true)
  }

  #handleKeyDown = (event) => {
    if (this.#slides.length < 2 || event.defaultPrevented) return
    if (event.altKey || event.ctrlKey || event.metaKey) return
    if (isEditableTarget(event.target)) return

    if (event.key === 'ArrowRight') {
      event.preventDefault()
      this.next()
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault()
      this.previous()
    }
  }

  #handleTouchStart = (event) => {
    if (event.touches?.length > 1) return
    const touch = event.touches?.[0]
    if (!touch) return
    this.#touchActive = true
    this.#touchStartX = touch.clientX
    this.#touchStartY = touch.clientY
  }

  #handleTouchEnd = (event) => {
    if (!this.#touchActive) return
    this.#touchActive = false
    const touch = event.changedTouches?.[0]
    if (!touch) return

    const deltaX = touch.clientX - this.#touchStartX
    const deltaY = touch.clientY - this.#touchStartY
    if (Math.abs(deltaX) < WipeSliderConfig.swipeThreshold) return
    if (Math.abs(deltaX) < Math.abs(deltaY)) return
    if (deltaX > 0) this.previous()
    else this.next()
  }

  #handleNativeScroll = () => {
    this.#stopAutoplay()
    window.clearTimeout(this.#nativeScrollEndTimer)
    this.#nativeScrollEndTimer = window.setTimeout(
      this.#settleNativeScroll,
      WipeSliderConfig.nativeSnapDelay
    )
  }

  #handleNativeScrollEnd = () => {
    window.clearTimeout(this.#nativeScrollEndTimer)
    this.#nativeScrollEndTimer = null
    this.#settleNativeScroll()
  }

  #handleVariantUpdate = (event) => {
    const { html, productId } = event.detail?.data ?? {}
    if (!(html && this.#matchesProduct(productId))) return

    const nextSlider = html.getElementById(this.id)
    if (nextSlider) this.innerHTML = nextSlider.innerHTML
    this.#mount()
  }

  #handleSlideEnd = (event) => {
    if (!WipeSliderTransitionProperties.includes(event.propertyName)) return
    if (event.target !== event.currentTarget) return
    this.#settleSlide()
  }

  #alignNativeTrack = () => {
    this.#scrollToNativeSlide(this.#currentIndex, true)
  }

  #settleNativeScroll = () => {
    const nextIndex = this.#getNativeScrollIndex()
    if (nextIndex === this.#currentIndex) {
      this.#syncAutoplay()
      return
    }
    this.#showImmediately(nextIndex)
  }

  #syncAutoplay = (delay = 0) => {
    this.#stopAutoplay()
    if (!this.#canAutoplay()) return
    if (delay > 0) {
      this.#autoplayTimer = window.setTimeout(this.#startAutoplay, delay)
      return
    }

    this.#startAutoplay()
  }

  #startAutoplay = () => {
    if (!this.#canAutoplay()) return

    let percent = 0
    const step =
      (100 / this.#autoplayInterval) * WipeSliderConfig.progressInterval

    this.#autoplayTimer = window.setInterval(() => {
      percent += step
      this.#setPaginationProgress(this.#currentIndex, Math.min(percent, 100))
      if (percent < 100) return

      this.#stopAutoplay()
      this.#fadePaginationProgress(this.#currentIndex)
      this.#navigate((this.#currentIndex + 1) % this.#slides.length, 1)
    }, WipeSliderConfig.progressInterval)
  }

  connectedCallback() {
    this.#mount()
  }

  disconnectedCallback() {
    this.#unmount()
  }

  #mount() {
    this.#unmount()
    this.#collectElements()
    this.#readSettings()
    this.#bindEvents()
    this.#observeViewport()
    this.#syncActiveState()
    this.#hydrateCurrentAndNext()
    this.#animateCurrentContent()
    if (this.#nativeSnap) requestAnimationFrame(this.#alignNativeTrack)
    this.setAttribute('loaded', '')
    this.#syncAutoplay(WipeSliderConfig.firstSlideDelay)
  }

  #unmount() {
    this.#stopAutoplay()
    this.#events?.abort()
    this.#events = null
    this.#unobserveViewport?.()
    this.#unobserveViewport = null
    this.#unwatchReduced?.()
    this.#unwatchReduced = null
    this.#detachTransitionListener()
    this.#contentAnimator.revert()
    window.clearTimeout(this.#nativeScrollEndTimer)
    this.#nativeScrollEndTimer = null
    this.#isAnimating = false
  }

  #collectElements() {
    this.#slides = Array.from(this.querySelectorAll(WipeSliderSelector.slide))
    this.#track = this.#slides[0]?.parentElement ?? null
    this.#controlsRoot = this.parentElement
    const currentIndex = this.#slides.findIndex(
      (slide) => slide.dataset.slideState === 'current'
    )
    this.#currentIndex = Math.max(0, currentIndex)
  }

  #readSettings() {
    const fallbackSeconds = WipeSliderConfig.autoplayInterval / 1000
    const seconds = parsePositiveNumber(
      this.dataset.autoplayInterval,
      fallbackSeconds
    )
    this.#autoplayInterval = seconds * 1000
    this.#autoplayEnabled = this.dataset.autoplay === 'true'
    this.#nativeSnap = this.hasAttribute('data-native-snap')
    this.#reducedMotion = useReduced()
    this.#isInViewport = false
    if (this.hasAttribute('data-keyboard') && !this.hasAttribute('tabindex')) {
      this.tabIndex = 0
    }
  }

  #bindEvents() {
    this.#events = new AbortController()
    const { signal } = this.#events

    this.#controlsRoot?.addEventListener('click', this.#handleClick, { signal })
    this.addEventListener('shopify:block:select', this.#handleBlockSelect, {
      signal
    })
    document.addEventListener('visibilitychange', this.#syncAutoplay, {
      signal
    })
    if (this.hasAttribute('data-keyboard')) {
      this.addEventListener('keydown', this.#handleKeyDown, { signal })
    }
    if (this.#nativeSnap) this.#bindNativeSnapEvents(signal)
    else if (this.hasAttribute('data-swipe')) this.#bindSwipeEvents(signal)
    if (this.hasAttribute('data-variant-updates')) {
      document.addEventListener('variant:update', this.#handleVariantUpdate, {
        signal
      })
    }
    this.#unwatchReduced = useReducedChange(this.#handleMotionChange)
    window
      .matchMedia(WipeSliderConfig.mobileQuery)
      .addEventListener('change', this.#handleBreakpointChange, { signal })
  }

  #bindSwipeEvents(signal) {
    this.addEventListener('touchstart', this.#handleTouchStart, {
      passive: true,
      signal
    })
    this.addEventListener('touchend', this.#handleTouchEnd, { signal })
    this.addEventListener('touchcancel', this.#handleTouchEnd, { signal })
  }

  #bindNativeSnapEvents(signal) {
    this.#track?.addEventListener('scroll', this.#handleNativeScroll, {
      passive: true,
      signal
    })
    this.#track?.addEventListener('scrollend', this.#handleNativeScrollEnd, {
      signal
    })
  }

  #observeViewport() {
    const viewport = useIntersection({
      rootMargin: this.dataset.visibilityMargin ?? '0px',
      threshold: 0.01
    })
    this.#unobserveViewport = viewport.observe(this, (entry) => {
      this.#isInViewport = entry?.isIntersecting === true
      this.#syncAutoplay()
    })
  }

  #matchesProduct(productId) {
    const currentProductId = this.dataset.productId
    return !(
      productId &&
      currentProductId &&
      `${productId}` !== currentProductId
    )
  }

  #navigate(targetIndex, direction, immediate = false) {
    if (this.#isAnimating && !immediate) return
    const nextIndex = this.#clampIndex(targetIndex)
    if (nextIndex === this.#currentIndex) {
      this.#syncAutoplay()
      return
    }

    const previousIndex = this.#currentIndex
    this.#currentIndex = nextIndex
    this.#stopAutoplay()
    this.#rewindPaginationProgress(previousIndex)
    this.#hydrateCurrentAndNext()
    if (this.#nativeSnap) {
      this.#scrollToNativeSlide(nextIndex, immediate)
      this.#showImmediately(nextIndex)
      return
    }
    if (immediate || this.#reducedMotion) {
      this.#showImmediately(nextIndex)
      return
    }
    this.#animateTransition(previousIndex, direction)
  }

  #animateTransition(previousIndex, direction) {
    const upcomingSlide = this.#slides[this.#currentIndex]
    if (!(this.#slides[previousIndex] && upcomingSlide)) return

    this.#detachTransitionListener()
    this.#isAnimating = true
    this.dataset.slideDirection = direction > 0 ? 'next' : 'prev'
    requestAnimationFrame(() => this.#commitSlide(previousIndex, upcomingSlide))
  }

  #commitSlide(previousIndex, upcomingSlide) {
    if (!this.#isAnimating) return
    this.#attachTransitionListener(upcomingSlide)
    this.#syncActiveState(previousIndex)
    this.#animateCurrentContent()
  }

  #attachTransitionListener(slide) {
    this.#transitionTarget = slide
    slide.addEventListener('transitionend', this.#handleSlideEnd)
    slide.addEventListener('transitioncancel', this.#handleSlideEnd)
  }

  #detachTransitionListener() {
    this.#transitionTarget?.removeEventListener(
      'transitionend',
      this.#handleSlideEnd
    )
    this.#transitionTarget?.removeEventListener(
      'transitioncancel',
      this.#handleSlideEnd
    )
    this.#transitionTarget = null
  }

  #settleSlide() {
    this.#detachTransitionListener()
    this.#isAnimating = false
    this.#syncActiveState()
    this.#hydrateCurrentAndNext()
    this.#syncAutoplay(WipeSliderConfig.resumeDelay)
  }

  #showImmediately(index) {
    this.#detachTransitionListener()
    this.#isAnimating = false
    this.#currentIndex = this.#clampIndex(index)
    this.#syncActiveState()
    this.#animateCurrentContent()
    this.#syncAutoplay()
  }

  #scrollToNativeSlide(index, immediate) {
    const slide = this.#slides[index]
    if (!(slide && this.#track)) return

    const trackRect = this.#track.getBoundingClientRect()
    const slideRect = slide.getBoundingClientRect()
    const left = this.#track.scrollLeft + slideRect.left - trackRect.left
    this.#track.scrollTo({
      behavior: immediate || this.#reducedMotion ? 'auto' : 'smooth',
      left
    })
  }

  #getNativeScrollIndex() {
    if (!(this.#track && this.#slides.length)) return this.#currentIndex

    const trackLeft = this.#track.getBoundingClientRect().left
    let closestIndex = 0
    let closestDistance = Number.POSITIVE_INFINITY
    for (const [index, slide] of this.#slides.entries()) {
      const distance = Math.abs(slide.getBoundingClientRect().left - trackLeft)
      if (distance < closestDistance) {
        closestDistance = distance
        closestIndex = index
      }
    }
    return closestIndex
  }

  #syncActiveState(leavingIndex = -1) {
    for (const [index, slide] of this.#slides.entries()) {
      const isCurrent = index === this.#currentIndex
      if (isCurrent) slide.dataset.slideState = 'current'
      else if (index === leavingIndex) slide.dataset.slideState = 'leaving'
      else delete slide.dataset.slideState
      slide.toggleAttribute('inert', !isCurrent)
      slide.setAttribute('aria-hidden', isCurrent ? 'false' : 'true')
      slide.dataset.index = `${index}`
      this.#syncSlideVideos(slide, isCurrent)
    }
    this.#syncNavigationButtons()
  }

  #syncSlideVideos(slide, isCurrent) {
    for (const video of slide.querySelectorAll('video[data-slide-video]')) {
      if (!(isCurrent && isRendered(video))) {
        video.pause()
        continue
      }
      if (video.preload === 'none') video.preload = 'auto'
      video.play().catch(() => null)
    }
  }

  #syncNavigationButtons() {
    const paginationItems = this.querySelectorAll(
      'button[data-wipe-slider-index]'
    )
    for (const item of paginationItems) {
      const index = Number(item.dataset.wipeSliderIndex)
      const isCurrent = index === this.#currentIndex
      item.classList.toggle('is-active', isCurrent)
      item.setAttribute('aria-current', `${isCurrent}`)
    }

    const buttons = this.#controlsRoot?.querySelectorAll(
      'button[aria-controls]'
    )
    for (const button of buttons ?? []) {
      if (button.getAttribute('aria-controls') !== this.id) continue
      button.disabled = false
    }
  }

  #hydrateCurrentAndNext() {
    if (!this.#slides.length) return
    this.#hydrateSlide(this.#currentIndex, 'high')
    if (this.#currentIndex < this.#slides.length - 1) {
      this.#hydrateSlide(this.#currentIndex + 1, 'auto')
    }
  }

  #hydrateSlide(index, priority) {
    const slide = this.#slides[this.#clampIndex(index)]
    if (!slide) return
    for (const source of slide.querySelectorAll('source[data-srcset]')) {
      source.srcset = source.dataset.srcset
      if (source.dataset.sizes) source.sizes = source.dataset.sizes
      delete source.dataset.srcset
      delete source.dataset.sizes
    }
    this.#hydrateImage(slide.querySelector('img'), priority)
  }

  #hydrateImage(image, priority) {
    if (!(image instanceof HTMLImageElement)) return
    image.loading = 'eager'
    image.fetchPriority = priority
    if (image.dataset.sizes) image.sizes = image.dataset.sizes
    if (image.dataset.srcset) image.srcset = image.dataset.srcset
    if (image.dataset.src) image.src = image.dataset.src
    delete image.dataset.sizes
    delete image.dataset.srcset
    delete image.dataset.src
  }

  #animateCurrentContent() {
    const content = this.#slides[this.#currentIndex]?.querySelector(
      WipeSliderSelector.content
    )
    this.#contentAnimator.animate(content, this.#reducedMotion)
  }

  #clampIndex(index) {
    const lastIndex = Math.max(0, this.#slides.length - 1)
    const numericIndex = Number.isFinite(index) ? index : 0
    return Math.min(Math.max(numericIndex, 0), lastIndex)
  }

  #canAutoplay() {
    return (
      this.#autoplayEnabled &&
      this.#slides.length > 1 &&
      !this.#reducedMotion &&
      !this.#isAnimating &&
      this.#isInViewport &&
      !document.hidden
    )
  }

  #stopAutoplay() {
    window.clearInterval(this.#autoplayTimer)
    window.clearTimeout(this.#autoplayTimer)
    this.#autoplayTimer = null
  }

  #setPaginationProgress(index, percent) {
    const fill = this.#paginationFill(index)
    if (!fill) return
    fill.style.opacity = '1'
    fill.style.width = `${percent}%`
  }

  #rewindPaginationProgress(index) {
    const fill = this.#paginationFill(index)
    if (!fill) return
    fill.style.transition = `width ${WipeSliderConfig.progressRewindDuration}ms ease-out`
    fill.style.width = '0%'
    window.setTimeout(() => {
      fill.style.transition = ''
    }, WipeSliderConfig.progressRewindDuration)
  }

  #fadePaginationProgress(index) {
    const fill = this.#paginationFill(index)
    if (!fill) return
    fill.style.opacity = '0'
    window.setTimeout(() => {
      fill.style.width = '0%'
    }, WipeSliderConfig.progressFadeDelay)
  }

  #paginationFill(index) {
    return this.querySelector(
      `button[data-wipe-slider-index="${index}"] .webgl-slider__progress-fill`
    )
  }
}

if (!customElements.get('wipe-slider')) {
  customElements.define('wipe-slider', WipeSlider)
}
